import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { createInitialUserValue } from './util';
import { getLocalStorageKey } from '../util';
import { refreshStaffOrAdminById, updateStaffSchoolById } from '@/api/user';
import Alerts from '@/services/alerts';

const storeId = 'root';
export const useRootStore = defineStore(
    storeId,
    () => {
        // State
        const user = ref(createInitialUserValue());
        const userToken = ref(null);
        const staffSchoolViewEnabled = ref(false);
        const backgroundOperations = ref([]);
        const backgroundOperationActions = ref([]);
        const pendingAlertCount = ref(0);

        // Getters
        const userIsStaff = computed(() => user.value.isStaff);
        const userSchool = computed(() => user.value.schoolRef || null);
        const userSchoolId = computed(() => user.value.schoolRef?.id || null);
        const userIsLoggedIn = computed(() => !!userToken.value);

        // Actions
        function updateUser(value) {
            user.value = {
                ...user.value,
                ...value
            };
        }

        function updateUserToken(value) {
            userToken.value = value;
        }
        /**
         * Fetches latest user data from the API and updates user in the store.
         * Depending on user type, one of two Django API endpoints will be used.
         * We have issue ENG-435 to consolidate these two endpoints into one Node API endpoint.
         */
        function refreshUser() {
            refreshStaffOrAdminById().then((data) => {
                updateUser({ ...data });
            });
        }

        function updateStaffSchoolViewEnabled(value) {
            staffSchoolViewEnabled.value = value;
        }

        /**
         * Updates staff user in DB, populates user state, and enables staff school view mode.
         */
        function updateStaffUserAndEnterSchoolMode(schoolId) {
            return updateStaffSchoolById({ schoolId }).then((data) => {
                updateUser({ ...data });
                updateStaffSchoolViewEnabled(true);
            });
        }

        function logout() {
            user.value = createInitialUserValue();
            userToken.value = null;
            staffSchoolViewEnabled.value = false;
            removeAllBackgroundOperations();
        }

        function updateBackgroundOperation(backgroundOperation) {
            const index = backgroundOperations.value.findIndex(
                (op) => op.id === backgroundOperation.id
            );
            // If this background operation doesn't exist yet, add it to state
            if (index === -1) {
                backgroundOperations.value = [...backgroundOperations.value, backgroundOperation];
            } else {
                // If this operation already exists, update it
                // (Return a new array instead of modifying the existing one)
                const elementsBefore = backgroundOperations.value.slice(0, index);
                const elementsAfter = backgroundOperations.value.slice(
                    index + 1,
                    backgroundOperations.value.length
                );
                backgroundOperations.value = [
                    ...elementsBefore,
                    backgroundOperation,
                    ...elementsAfter
                ];
            }
        }

        function removeBackgroundOperation(backgroundOperation) {
            backgroundOperations.value = backgroundOperations.value.filter(
                (op) => op.id != backgroundOperation.id
            );

            backgroundOperationActions.value = backgroundOperationActions.value.filter(
                (item) => item.operationId != backgroundOperation.id
            );
        }

        function removeAllBackgroundOperations() {
            backgroundOperations.value = [];
            backgroundOperationActions.value = [];
        }

        function updatePendingAlertCount(count) {
            pendingAlertCount.value = count;
        }

        async function refreshPendingAlertCount() {
            const fetchedAlertCount = await Alerts.api.getPendingAlertCount();
            updatePendingAlertCount(fetchedAlertCount);
        }

        return {
            // State
            user,
            userToken,
            staffSchoolViewEnabled,
            backgroundOperations,
            backgroundOperationActions,
            pendingAlertCount,
            // Getters
            userIsStaff,
            userSchool,
            userSchoolId,
            userIsLoggedIn,
            // Actions
            updateUser,
            updateUserToken,
            refreshUser,
            updateStaffSchoolViewEnabled,
            updateStaffUserAndEnterSchoolMode,
            logout,
            updateBackgroundOperation,
            removeBackgroundOperation,
            refreshPendingAlertCount
        };
    },
    {
        // To match the old Vuex setup, we persist the entire state to local storage.
        // In the future, we may want to only persist certain parts of the state.
        persist: {
            key: getLocalStorageKey({ storeId })
        }
    }
);
